import { useAtomValue } from 'jotai';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { GetRowIdParams, GridApi, GridOptions, GridReadyEvent, IRowNode } from 'ag-grid-community';
import { selectedCompanyIdStateFinanceJ } from '../../../services/state/PortCosStateJ';
import {
  allInvestmentRoundsMapStateJ,
  financeTransactionsByCompanyStateLoadableJ,
} from '../state/FinanceStateJ';
import { useExtendedExportSettings } from '../../../components/AgTable/exportToExcelDefs';
import {
  RoundCellRenderer,
  RoundCellRendererName,
} from '../../../components/AgTable/cell-renderers/RoundCellRenderer';
import { Loader } from '../../../components/Loader/Loader';
import { AgTable } from '../../../components/AgTable/AgTable';
import { genericComparator } from '../../../util/comparators';
import { ErrorOccurredMessage } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { TransactionModals } from '../Transactions/modals/TransactionModals';
import { useGetTransactionGridColdDefsForTypeId } from '../../../schemas/transactions/TransactionSchemaUtil';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { trackEvent } from '../../../util/datadogTracking';
import { AllTransactionsItem, AvailableTransactionTypePills } from './AvailableTransactionTypesPills';

export const TransactionsTable3: FC = () => {
  const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState(AllTransactionsItem.value);
  const selectedCompanyId = useAtomValue(selectedCompanyIdStateFinanceJ);
  const company = useAtomValue(companyStateJ(selectedCompanyId ?? -1));
  const { status, rowData } = useTransactionRowData(selectedCompanyId ?? -1);
  const getSchemaForTransactionTypeId = useGetTransactionGridColdDefsForTypeId();
  const { getRowId } = useCommonTransactionGridDefs();
  const { exportParams } = useExtendedExportSettings(new Set(), `${company?.name ?? ''}_Transactions`);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridAPI(event.api);
  }, []);

  // Fire when the Transactions area opens for a selected company (FD-3587).
  // Guarded on selectedCompanyId so empty mounts (no company in context) don't
  // inflate the view count; fires once per company the area is opened for.
  useEffect(() => {
    if (selectedCompanyId == null) {
      return;
    }
    trackEvent('transactions.viewed');
  }, [selectedCompanyId]);

  const initialGridOptions = useMemo(() => {
    const options: GridOptions = {
      autoGroupColumnDef: {
        comparator: genericComparator,
      },
      columnDefs: [],
      components: {
        [RoundCellRendererName]: RoundCellRenderer,
      },
      defaultColDef: {
        comparator: genericComparator,
        flex: 1,
        cellStyle: {
          color: 'red',
        },
        filterParams: {
          buttons: ['reset'],
          newRowsAction: 'keep',
        },
        suppressAutoSize: false,
        suppressMovable: true,
      },

      doesExternalFilterPass: () => true,
      isExternalFilterPresent: () => true,
      onGridReady,
      rowData: [],
      rowGroupPanelShow: 'never',
    };

    return options;
  }, [onGridReady]);

  useEffect(() => {
    const colDefs = getSchemaForTransactionTypeId(selectedTransactionType);
    gridAPI?.setGridOption('columnDefs', colDefs);
  }, [getSchemaForTransactionTypeId, gridAPI, selectedTransactionType]);

  useEffect(() => {
    gridAPI?.onFilterChanged();
  }, [selectedTransactionType, gridAPI]);

  const onTypeFilterChanged = useCallback((item: ISimpleChoice<number>) => {
    setSelectedTransactionType(item.value);
  }, []);

  const isExternalFilterPresent = useCallback(() => {
    return selectedTransactionType !== -1;
  }, [selectedTransactionType]);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<ITransactionDataModel>) => {
      return node.data?.transactionTypeId === selectedTransactionType;
    },
    [selectedTransactionType]
  );

  if (status === 'loading') {
    return <Loader height='80%' />;
  }
  if (status === 'hasError') {
    return <ErrorOccurredMessage />;
  }

  return (
    <>
      <AvailableTransactionTypePills
        onChange={onTypeFilterChanged}
        selectedItemId={selectedTransactionType}
        transactions={rowData}
      />
      <FadeInGridWrapper key={selectedTransactionType}>
        <div style={{ paddingTop: '1rem', height: '63vh', paddingBottom: '1rem' }}>
          <AgTable
            autoSizeStrategy={initialGridOptions.autoSizeStrategy}
            rowData={rowData}
            gridOptions={initialGridOptions}
            key={selectedCompanyId}
            defaultCsvExportParams={exportParams}
            defaultExcelExportParams={exportParams}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            rowGroupPanelShow={'never'}
            getRowId={getRowId}
            grandTotalRow='bottom'
          />
        </div>
      </FadeInGridWrapper>
      <TransactionModals />
    </>
  );
};

function useTransactionRowData(selectedCompanyId: number) {
  const investmentRounds = useAtomValue(allInvestmentRoundsMapStateJ);

  const transactionsLoadable = useAtomValue(
    financeTransactionsByCompanyStateLoadableJ(selectedCompanyId ?? -1)
  );

  if (transactionsLoadable.state === 'loading') {
    return {
      status: transactionsLoadable.state,
      rowData: [] as ITransactionDataModel[],
    };
  }
  if (transactionsLoadable.state === 'hasError') {
    return {
      status: transactionsLoadable.state,
      rowData: [] as ITransactionDataModel[],
    };
  }

  return {
    status: transactionsLoadable.state,
    rowData: transactionsLoadable.data?.map((transaction) => {
      return {
        ...transaction,
        roundId: investmentRounds.get(transaction.investmentRoundId ?? -1)?.roundId,
      };
    }) as ITransactionDataModel[],
  };
}

export function useCommonTransactionGridDefs() {
  const getRowId = useCallback(
    (params: GetRowIdParams<ITransactionDataModel>) => String(params.data?.id),
    []
  );

  return { getRowId };
}
